import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { fetchBlogPosts } from "../redux/blogSlice";
import Navbar from "../components/Navbar";
import CrawlLink from "../components/CrawlLink";
import { dropStaticDuplicates } from "../components/SEO/SEOHead";
import { CURRENCY_LOCALE, SITE_ORIGIN } from "../config/market";
// import Footer from "../components/Footer";

const CATEGORIES = [
  { value: "", label: "All" },
  { value: "announcement", label: "Announcements" },
  { value: "strategy", label: "Strategy" },
  { value: "product", label: "Product" },
  { value: "engineering", label: "Engineering" },
  { value: "policy", label: "Policy" },
  { value: "other", label: "Other" },
];

const CATEGORY_COLORS = {
  announcement: "#e3f2fd",
  strategy: "#fff3e0",
  product: "#e8f5e9",
  engineering: "#f3e5f5",
  policy: "#fce4ec",
  other: "#f5f5f5",
};

const CATEGORY_TEXT = {
  announcement: "#1565c0",
  strategy: "#e65100",
  product: "#2e7d32",
  engineering: "#6a1b9a",
  policy: "#c62828",
  other: "#424242",
};

/* Public-facing web origin for canonical URLs and OG tags. Read directly
   from REACT_APP_WEB_URL so it stays decoupled from REACT_APP_SERVER_URL —
   the API is now reached via api.lusuku.ug. The hardcoded fallback keeps
   deploys safe if the env var is ever cleared. Same pattern as
   netlify/edge-functions/og-inject.js. */
const WEB_URL = process.env.REACT_APP_WEB_URL || SITE_ORIGIN;

const BlogListPage = () => {
  const dispatch = useDispatch();
  const { posts, isFetching, total } = useSelector((state) => state.blog);
  const [activeCategory, setActiveCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    dispatch(fetchBlogPosts({ category: activeCategory }));
  }, [dispatch, activeCategory]);

  const filtered = posts.filter(
    (p) =>
      !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.author?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(CURRENCY_LOCALE, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      {/* dropStaticDuplicates: index.html ships its own description and
          og:url, and Helmet only manages the tags it wrote, so without it the
          page carried two of each (see SEOHead). */}
      <Helmet onChangeClientState={dropStaticDuplicates}>
        <title>Lusuku Blog: Insights and Announcements</title>
        <meta
          name="description"
          content="Official announcements, strategy papers, and product updates from Lusuku — the online marketplace."
        />
        <meta property="og:title" content="Lusuku Blog: Insights and Announcements" />
        <meta
          property="og:description"
          content="Official announcements, strategy papers, and product updates from Lusuku."
        />
        <meta property="og:url" content={`${WEB_URL}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${WEB_URL}/lusuku-og-default.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`${WEB_URL}/blog`} />
      </Helmet>

      <Navbar />

      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroEyebrow>Official Communications</HeroEyebrow>
            <HeroTitle>Insights & Announcements</HeroTitle>
            <HeroSubtitle>
              Strategy papers, product updates, and governance decisions from
              the Lusuku team.
            </HeroSubtitle>
            <SearchBar>
              <span style={{ fontSize: "1rem", marginRight: "0.1rem" }}>🔍</span>
              <SearchInput
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
          </HeroContent>
        </HeroSection>

        <ContentWrapper>
          <FilterRow>
            {CATEGORIES.map((cat) => (
              <FilterChip
                key={cat.value}
                $active={activeCategory === cat.value}
                onClick={() => setActiveCategory(cat.value)}
              >
                {cat.label}
              </FilterChip>
            ))}
          </FilterRow>

          {isFetching ? (
            <LoadingGrid>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <SkeletonCard key={n}>
                  <SkeletonImg />
                  <SkeletonLine $width="60%" $height="12px" />
                  <SkeletonLine $width="90%" $height="20px" />
                  <SkeletonLine $width="100%" $height="14px" />
                  <SkeletonLine $width="80%" $height="14px" />
                </SkeletonCard>
              ))}
            </LoadingGrid>
          ) : filtered.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📄</EmptyIcon>
              <EmptyTitle>No posts found</EmptyTitle>
              <EmptyText>
                {searchQuery
                  ? "Try a different search term."
                  : "Check back soon for updates."}
              </EmptyText>
            </EmptyState>
          ) : (
            <>
              <ResultMeta>
                {total} post{total !== 1 ? "s" : ""} published
              </ResultMeta>
              <PostsGrid>
                {filtered.map((post) => (
                  /* A real link (CrawlLink), not a click handler: Google
                     follows links only, so the articles were reachable from
                     the sitemap alone. A tap still opens the article in-app. */
                  <PostCard
                    key={post._id}
                    as={CrawlLink}
                    to={`/blog/${post.slug}`}
                  >
                    <CardTop $bg={CATEGORY_COLORS[post.category] || "#f5f5f5"}>
                      <CategoryBadge
                        $bg={CATEGORY_COLORS[post.category] || "#f5f5f5"}
                        $color={CATEGORY_TEXT[post.category] || "#424242"}
                      >
                        {post.category || "post"}
                      </CategoryBadge>
                      {post.ogImage ? (
                        <OgImage src={post.ogImage} alt={post.title} />
                      ) : (
                        <PlaceholderIcon>
                          {post.category === "strategy"
                            ? "📊"
                            : post.category === "engineering"
                            ? "⚙️"
                            : post.category === "policy"
                            ? "📋"
                            : post.category === "product"
                            ? "🚀"
                            : "📣"}
                        </PlaceholderIcon>
                      )}
                    </CardTop>

                    <CardBody>
                      <PostMeta>
                        <span>{formatDate(post.publishedAt)}</span>
                        <Dot />
                        <span>{post.author || "Lusuku Team"}</span>
                      </PostMeta>
                      <PostTitle>{post.title}</PostTitle>
                      {post.excerpt && (
                        <PostExcerpt>{post.excerpt}</PostExcerpt>
                      )}
                      <ReadMore>Read more →</ReadMore>
                    </CardBody>
                  </PostCard>
                ))}
              </PostsGrid>
            </>
          )}
        </ContentWrapper>
      </PageContainer>

      {/* <Footer /> */}
    </>
  );
};

export default BlogListPage;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
  padding-top: var(--lusuku-nav-h, 120px);
  @media (max-width: 850px) {
    padding-top: var(--lusuku-nav-h, 130px);
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%);
  padding: 4rem 1.5rem 3rem;
  color: white;
`;

const HeroContent = styled.div`
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
`;

const HeroEyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.75rem;
`;

const HeroTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  line-height: 1.2;
  @media (max-width: 600px) {
    font-size: 1.9rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.85);
  max-width: 600px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 50px;
  padding: 0.6rem 1.25rem;
  max-width: 480px;
  margin: 0 auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.95rem;
  color: #333;
  background: transparent;
  &::placeholder {
    color: #aaa;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const FilterChip = styled.button`
  padding: 0.45rem 1rem;
  border-radius: 50px;
  border: 2px solid ${(p) => (p.$active ? "#1976d2" : "#e0e0e0")};
  background: ${(p) => (p.$active ? "#1976d2" : "white")};
  color: ${(p) => (p.$active ? "white" : "#555")};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: #1976d2;
    color: ${(p) => (p.$active ? "white" : "#1976d2")};
  }
`;

const ResultMeta = styled.p`
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 1.5rem;
  font-weight: 500;
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.75rem;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PostCard = styled.article`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  &:hover {
    transform: translateY(-5px);
    box-shadow:
      0 18px 46px rgba(34, 176, 38, 0.24),
      0 8px 28px rgba(25, 118, 210, 0.12);
  }
`;

const CardTop = styled.div`
  background: #ffffff;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => p.$color}44;
`;

const OgImage = styled.img`
  position: relative;
  z-index: 1;
  width: calc(100% - 36px);
  height: calc(100% - 28px);
  object-fit: contain;
  background: transparent;
  box-shadow:
    0 0 0 3px rgba(67, 214, 48, 0.58),
    0 2px 4px rgba(42, 190, 36, 0.48),
    0 6px 10px rgba(34, 176, 38, 0.3),
    0 10px 14px rgba(34, 176, 38, 0.16);
`;

const PlaceholderIcon = styled.div`
  font-size: 3.5rem;
  opacity: 0.6;
`;

const CardBody = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #999;
  margin-bottom: 0.6rem;
`;

const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #ccc;
  display: inline-block;
`;

const PostTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.4;
  margin-bottom: 0.6rem;
`;

const PostExcerpt = styled.p`
  font-size: 0.88rem;
  color: #666;
  line-height: 1.6;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMore = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: #1976d2;
  margin-top: 1rem;
  display: inline-block;
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.75rem;
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const skeletonBase = `
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: shimmer 1.4s ease infinite;
  border-radius: 6px;
`;

const SkeletonImg = styled.div`
  ${skeletonBase}
  height: 160px;
  border-radius: 10px;
`;

const SkeletonLine = styled.div`
  ${skeletonBase}
  width: ${(p) => p.$width || "100%"};
  height: ${(p) => p.$height || "14px"};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 1rem;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.4rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
  color: #888;
  font-size: 1rem;
`;
